import { ActionButton } from "@src/components/deprecated/ActionButton/ActionButton";
import { Checkbox } from "@src/components/ui-components/Checkbox/Checkbox";
import { ActionsPopover } from "../../../../components/deprecated/ActionsPopover/ActionsPopover";
import { DataTableData, DataTableHeader } from "../../../../components/deprecated/DataTable/DataTable";
import { ActionsPopoverAction } from "../../../../components/ui-components/AdvancedSelectInput/AdvancedSelectInput";
import { DataObjectField } from "../DataObjectField/DataObjectField";
import { FirebaseData } from "../FirebaseData";

export type DataObjectType = { [key: string]: any };
export type DataObjectAction<T extends DataObjectType> = {
  id: string;
  title: string;
  iconName: string;
  iconColor?: string;
  requiredConsent?: (dataObjects: T[]) => Promise<{ dataObjects: DataObjectType[]; data?: any }>;
  performAction?: (data?: any) => Promise<any>;
  performActionOnDataObjects?: (dataObjects: T[], data?: any) => Promise<any>;
  maxSelected?: number;
  link?: string;
};

export abstract class DataObjectHelper<T extends DataObjectType> {
  /** Required methods */
  abstract id(): string;

  abstract allFields(): DataObjectField<any, T>[];

  abstract displayName(): string;

  displayNamePlural(): string {
    return this.displayName();
  }
  abstract getAllDataTableDatas(documents: T[], fields?: string[]): DataTableData[];
  getUniqueIDForData(data: T): string {
    if (data instanceof FirebaseData) return data.id;
    else if (typeof data === "object") {
      if (data.id) return data.id;
      else if (data.key) return data.key;
    }
    return "ERROR";
  }

  /** Optional methods */
  getAllDatas(): T[] {
    return [];
  }

  areAllLoaded(): boolean {
    return true;
  }

  loadAll(): Promise<T[]> {
    return Promise.resolve([]);
  }

  getLinkTo(key: string): string | undefined {
    return undefined;
  }

  /** Default methods */
  getField(key: string | undefined): DataObjectField<any, T> | undefined {
    return this.allFields().find((field) => field.key === key);
  }

  /** DataTable-methods */
  getDataTableDatas(
    documents: T[],
    enabledFields: string[] | undefined = undefined,
    selection: { selectedFields: string[] | "all"; didCheck: (key: string, checked: boolean) => void } | undefined = undefined,
    refreshTable: (() => void) | undefined = undefined
  ): DataTableData[] {
    const addSelectionToDatas = (datas: DataTableData[], documents: T[]): DataTableData[] => {
      return datas.map((element, index) => {
        const returnData: DataTableData = { ...element };
        if (this.getActionsForData !== undefined) {
          const dataObject = documents[index];
          if (dataObject) {
            const actions: ActionsPopoverAction[] = this.getActionsForData(dataObject).map((action) => {
              return {
                title: action.title,
                iconName: action.iconName,
                iconColor: action.iconColor,
                link: action.link,
                dataCy: `actions-${action.id}-option`,
                onClick: () => {
                  if (action.requiredConsent) {
                    action
                      .requiredConsent([dataObject])
                      .then((data) => {
                        if (action.performActionOnDataObjects !== undefined) {
                          return action.performActionOnDataObjects?.([dataObject], data.data);
                        } else {
                          return action.performAction?.(data.data);
                        }
                      })
                      .then(() => refreshTable?.());
                  } else {
                    if (action.performActionOnDataObjects !== undefined) {
                      return action.performActionOnDataObjects?.([dataObject]).then(() => refreshTable?.());
                    } else {
                      return action.performAction?.().then(() => refreshTable?.());
                    }
                  }
                },
              };
            });
            returnData.cells["actions"] = {
              element: <ActionsPopover actions={actions} trigger={<ActionButton dataCy={`table-row-action-button-${index}`} />} />,
            };
          }
          if (selection) {
            returnData.cells["selection"] = {
              element: (
                <Checkbox
                  value={selection.selectedFields === "all" || selection.selectedFields.includes(element.key)}
                  onChange={(checked) => selection.didCheck(element.key, checked)}
                />
              ),
              rawData: selection.selectedFields === "all" || selection.selectedFields.includes(element.key),
            };
          }
        }
        return returnData;
      });
    };

    if (!enabledFields) return addSelectionToDatas(this.getAllDataTableDatas(documents), documents);

    return addSelectionToDatas(
      this.getAllDataTableDatas(documents, enabledFields).map((element) => {
        const returnData: DataTableData = {
          key: element.key,
          cells: {},
        };
        for (const field of Object.keys(element.cells).filter((key) => enabledFields.includes(key))) {
          returnData.cells[field] = element.cells[field];
        }
        return returnData;
      }),
      documents
    );
  }

  getDataTableHeaders(
    enabledFields: string[] | undefined = undefined,
    hiddenKeys: string[] | undefined = undefined,
    selectionData: { selected: boolean; didSelect: (selected: boolean) => void } | undefined = undefined,
    hideActions: boolean = false,
    dataCy?: string
  ): DataTableHeader[] {
    let elements = this.getAllDataTableHeaders();

    // Removes hidden keys
    if (hiddenKeys) {
      elements = elements.filter((element) => !hiddenKeys.includes(element.key));
    }

    // Filters enabled fields
    if (enabledFields?.length) {
      elements = elements
        .filter((field) => enabledFields.includes(field.key))
        .sort((a, b) => enabledFields.indexOf(a.key) - enabledFields.indexOf(b.key));
    } else {
      // Filter standard fields if specified
      const standardHeaderElements = this.getStandardHeaderElements();
      if (standardHeaderElements.length > 0) {
        elements = elements
          .filter((element) => standardHeaderElements.includes(element.key))
          .sort((a, b) => standardHeaderElements.indexOf(a.key) - standardHeaderElements.indexOf(b.key));
      }
    }

    if (this.getActionsForData !== undefined) {
      if (selectionData) {
        elements.unshift({
          key: "selection",
          title: "",
          width: "30px",
          customComponent: (
            <Checkbox
              data-cy={dataCy && `${dataCy}-select-all-checkbox`}
              value={selectionData.selected}
              onChange={(checked) => selectionData.didSelect(checked)}
            />
          ),
        });
      }
      if (!hideActions) {
        elements.push({ key: "actions", title: "", icon: "more_horiz" });
      }
    }

    return elements;
  }

  getAllDataTableHeaders(): DataTableHeader[] {
    const result: DataTableHeader[] = [];
    for (const field of this.allFields()) {
      result.push({ key: field.key, title: field.title, icon: field.iconName });
    }
    return result;
  }

  getStandardHeaderElements(): string[] {
    return [];
  }

  getDefaultTableSortKey(): string | undefined {
    return undefined;
  }

  getDefaultTableSortDirection(): "ascending" | "descending" {
    return "ascending";
  }

  getDefaultNameForData(data: T): string {
    const nameField =
      this.allFields().find((field) => field.key === "name") ?? this.allFields().find((field) => field.key === "title") ?? this.allFields()[0];
    return nameField.getStringRepresentation(nameField.dataGetter(data)) ?? "";
  }

  /** Get default filter fields */
  getDefaultFilterFields?: () => DataObjectField<any, any>[];

  /** Native actions */
  getActionsForData?: (data: T, selectedDatas?: T[]) => DataObjectAction<T>[];

  /** Saving update */
  saveUpdate(data: T, updatedRawData: { [key: string]: any }): Promise<any> {
    return Promise.reject("Not implemented");
  }
}
